/**
 * Client error reporting.
 *
 * Firebase has NO Crashlytics for web — it is an Android/iOS product — so a
 * browser crash has to go somewhere else. This sends errors to two places:
 *
 *   1. GA4 as an `exception` event. Cheap, gives volume and lets you segment
 *      crashes by auth_state, but GA4 truncates and does not group stacks.
 *   2. POST /client-errors on goldfinder-api-v2, which writes a structured
 *      entry to Cloud Logging. Those surface in GCP Error Reporting GROUPED
 *      WITH the API's own errors — same project, same console, one place to
 *      look when a user says "the map went white".
 *
 * Reporting is best-effort by construction: it must never throw, never block
 * rendering, and never retry hard enough to make a bad situation worse.
 */
import { track } from './telemetry';
import { auth } from './firebase';

const enabled = process.env.NODE_ENV === 'production';
const API_URL = process.env.REACT_APP_GOLD_FINDER_API_URL;

// A crash inside a render loop or a moveend handler can fire hundreds of times
// a second. Cap what leaves the browser: identical errors are sent once, and
// the whole session is capped, so a runaway loop cannot DoS the API or burn
// the GA4 event quota.
const MAX_PER_SESSION = 25;
const seen = new Set();
let sentCount = 0;

function signature(message, stack) {
  return `${message}::${(stack || '').split('\n').slice(0, 3).join('|')}`;
}

/**
 * Report an error.
 *
 * @param {Error|string} error
 * @param {object} context  Extra fields — component, layer, action. Keep these
 *                          free of user data; they end up in Cloud Logging.
 */
export function logError(error, context = {}) {
  try {
    const err = error instanceof Error ? error : new Error(String(error));
    const message = err.message || 'Unknown error';
    const stack = err.stack || '';

    if (!enabled) {
      console.error('[logError]', message, context, err);
      return;
    }

    const sig = signature(message, stack);
    if (seen.has(sig) || sentCount >= MAX_PER_SESSION) return;
    seen.add(sig);
    sentCount += 1;

    // GA4 reserves `exception` with a `description` param.
    track('exception', {
      description: message.slice(0, 100),
      fatal: context.fatal ? 'true' : 'false',
      component: context.component || 'unknown',
    });

    if (!API_URL) return;

    const payload = {
      message,
      stack,
      // Error Reporting groups on the stack, so send a synthetic one when the
      // browser gave us a bare string (common for cross-origin script errors).
      url: window.location.href,
      userAgent: navigator.userAgent,
      uid: auth.currentUser?.uid || null,
      timestamp: new Date().toISOString(),
      context,
    };

    // keepalive lets the request outlive a navigation, which matters because
    // a fatal error is often followed immediately by the user closing the tab.
    fetch(`${API_URL}/client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      /* reporting the reporter's failure would recurse */
    });
  } catch {
    /* never let telemetry throw */
  }
}

/**
 * Install global handlers. Call once, as early as possible in index.js.
 *
 * These catch what an ErrorBoundary structurally cannot: errors thrown outside
 * React's render phase — event handlers, map callbacks, async WFS code, and
 * rejected promises nobody awaited.
 */
export function installGlobalErrorHandlers() {
  window.addEventListener('error', event => {
    // Resource load failures (a 404 image) arrive here with no `error` object
    // and are not worth reporting as crashes.
    if (!event.error) return;
    logError(event.error, { component: 'window.onerror', fatal: true });
  });

  window.addEventListener('unhandledrejection', event => {
    const reason = event.reason;
    // Aborted WFS requests are a normal part of map panning — the request
    // manager cancels in-flight queries on every move. Not errors.
    if (reason?.name === 'AbortError' || reason?.name === 'CanceledError' || reason?.code === 'ERR_CANCELED') {
      return;
    }
    logError(reason, { component: 'unhandledrejection', fatal: false });
  });
}
